import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { BreadCrumbArrowIcon, SuccessIcon } from '@native-instruments/webex-ni';
import clsx from 'clsx';

import { ConditionalLink } from '@app/components/Link';
import { SUBSCRIPTION_CHECKOUT_PATHS } from '@config/routes.config';

import messages from '../../Subscription.messages';

const STEPS = ['account', 'address', 'payment'] as const;

type Step = (typeof STEPS)[number];

enum StepStatus {
  Complete = 'complete',
  Default = 'default',
  Current = 'current',
}

export function Stepper() {
  const { formatMessage } = useIntl();
  const steps = useSteps();

  return (
    <ol
      data-testid="subscription-stepper"
      className={clsx(
        'flex flex-between justify-center items-center z-1',
        'col-start-1 col-span-4 lg:col-span-8 xl:col-span-12 py-2',
      )}
    >
      {steps.map(({ message, path, status }, index) => (
        <li
          data-testid={`step-${index}-${status}`}
          key={path}
          className={clsx(
            'flex font-medium text-xs text-grey-60 text-center',
            'mr-1 md:mr-2 lg:mr-4 last:mr-0',
            status === StepStatus.Current && 'text-grey-90',
            status === StepStatus.Complete && 'text-success',
          )}
        >
          <ConditionalLink
            condition={status !== StepStatus.Default}
            to={path}
            className="contents"
          >
            <div
              className="flex align-center justify-center items-center"
              data-testid={`step-${status}`}
            >
              {status === StepStatus.Complete ? (
                <span
                  className={clsx(
                    'transition ease-in-out',
                    'inline-flex justify-center items-center  mr-0.5',
                  )}
                >
                  <SuccessIcon className="w-3 h-3" />
                </span>
              ) : (
                <span className="mr-0.5">{`${index + 1}.`}</span>
              )}
              {formatMessage(message)}
            </div>
          </ConditionalLink>

          {index < steps.length - 1 && (
            <BreadCrumbArrowIcon className="self-center ml-1 md:ml-2 lg:ml-4" />
          )}
        </li>
      ))}
    </ol>
  );
}

function useSteps() {
  const { pathname } = useLocation();
  const { subscriptionIntentId } = useParams<{
    subscriptionIntentId: string;
  }>();

  return STEPS.map((name) =>
    generateStep(name, pathname, subscriptionIntentId),
  );
}

function generateStep(
  name: Step,
  pathname: string,
  subscriptionIntentId: string,
) {
  const nextSteps = STEPS.slice(STEPS.indexOf(name) + 1, STEPS.length);
  return {
    message: messages[name],
    // account step should not redirect
    // is only informative for the user that they did that step
    path:
      name === 'account'
        ? null
        : `${SUBSCRIPTION_CHECKOUT_PATHS[name]}/${subscriptionIntentId}`,
    status: nextSteps.some((step) => pathname.includes(step))
      ? StepStatus.Complete
      : pathname.includes(name)
        ? StepStatus.Current
        : StepStatus.Default,
  };
}
